import type { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

// Components
import HelpArticle from '@/components/pages/HelpCenter/components/Article'
import HelpHead from '@/components/pages/HelpCenter/components/HelpHead'

// Libs
import { fetchHelpArticle, FetchHelpArticleResponse } from '@/api/fetchHelpArticle'
import {
  articleDescription,
  countryQueryOf,
  getHelpCountry,
  helpArticlePath,
  isHelpNotFoundError,
  parseHelpId,
  requireHelpData,
} from '@/components/pages/HelpCenter/methods'

type Props = {
  data: FetchHelpArticleResponse
  countryCode: string
  countryQuery: string | null
}

// _app 以 <Component {...appProps} /> 渲染，getServerSideProps 的資料在 props.pageProps
const HelpArticlePage = ({ pageProps }: { pageProps: Props }) => {
  const { data, countryCode, countryQuery } = pageProps

  return (
    <>
      <HelpHead title={data.title} description={articleDescription(data.summary, data.content)} canonicalPath={helpArticlePath(data.id)} />
      <HelpArticle data={data} countryCode={countryCode} countryQuery={countryQuery ?? undefined} />
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ locale, params, query }) => {
  const id = parseHelpId(params?.id)
  if (!id) return { notFound: true }

  const countryCode = getHelpCountry(locale, query.country_code)
  try {
    const data = requireHelpData(await fetchHelpArticle({ id, countryCode }))
    return {
      props: {
        data,
        countryCode,
        countryQuery: countryQueryOf(query.country_code) ?? null,
        ...(await serverSideTranslations(locale as string, ['common'])),
      },
    }
  } catch (error) {
    if (isHelpNotFoundError(error)) return { notFound: true }
    throw error
  }
}

export default HelpArticlePage
